import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { DialogTitle, DialogContent, Dialog, Button, DialogActions } from '@mui/material';
import { validateProductGroup } from '@/helpers/administrationUtils';
import { ERROR } from '@/helpers/constants';
import { toCamelCase } from '@/helpers/utils';
import useGlobalSpinnerContext from '@/hooks/useGlobalSpinner';
import useToastContext from '@/hooks/useToastContext';
import { getPlantsForm } from '@/redux/Forms/forms.reducer';
import administrationServices from '@/services/administrationServices';
import InputText from '../UI/InputText/InputText';
import MultiSelection from '../UI/MultiSelection/MultiSelection';
import UsersSelection from '../UI/UsersSelection/UsersSelection';
import styles from './DialogProductGroup.module.scss';

const requiredFields = ['name', 'plants', 'coordinators'];

type ProductGroupForm = {
  name: string;
  plants: { wkzNumber: string; plantName: string }[];
  coordinators: string[];
};

const INITIAL_FORM_VALUE: ProductGroupForm = {
  name: '',
  plants: [],
  coordinators: [],
};

const DialogProductGroup = (props) => {
  const { close, title, reloadProductGroups, plants } = props;
  const { t } = useTranslation();
  const { addToast } = useToastContext();
  const { setLoading } = useGlobalSpinnerContext();
  const [productGroup, setProductGroup] = useState<ProductGroupForm>(INITIAL_FORM_VALUE);

  const handlePlantChange = (inputName, newValue) => {
    setProductGroup((prevState) => ({ ...prevState, [inputName]: newValue }));
  };

  const fieldsCompleted = useMemo(() => requiredFields.every((fieldToCheck) => {
    const fieldValue = productGroup[fieldToCheck];

    if (Array.isArray(fieldValue)) return fieldValue.length > 0;
    if (typeof fieldValue === 'string') return fieldValue.trim().length > 0;

    return Boolean(fieldValue);
  }), [productGroup]);

  const saveProductGroup = () => {
    if (validateProductGroup(productGroup, addToast, t)) {
      setLoading(true);
      const newProductGroup = {
        ...productGroup,
        plants: productGroup.plants.map((plant) => plant.wkzNumber),
        mailboxes: [],
      };
      administrationServices.createProductGroup(newProductGroup)
        .then(reloadProductGroups)
        .catch((error) => addToast(t(`administration.productGroup.${toCamelCase(error.code)}`, { name: newProductGroup.name }), ERROR))
        .finally(() => setLoading(false));
    }
  };

  const handleDeleteChip = (chipToDelete) => {
    const newSelections = productGroup.plants.filter((model) => (model.plantName !== chipToDelete));
    handlePlantChange('plants', newSelections);
  };

  return (
    <Dialog open maxWidth="md" onClose={close} aria-labelledby="form-dialog-title">
      <DialogTitle id="form-dialog-title">{title}</DialogTitle>
      <DialogContent className={styles.DialogContent}>
        <div className={styles.DialogProductGroup}>
          <div className={styles.PageContent}>
            <div className={styles.Fields}>
              <InputText
                id="create-product-group-name-text"
                value={productGroup.name}
                isMandatory
                label={t('administration.productGroup.productGroup')}
                handleChange={(ev) => handlePlantChange('name', ev.target.value)}
              />
              <MultiSelection
                handleDeleteItem={handleDeleteChip}
                isMandatory
                id="create-product-group-plants-multiselect"
                label={t('administration.productGroup.dedicatedPlants')}
                options={plants || []}
                handleChange={(event) => handlePlantChange('plants', event.target.value)}
                value={productGroup.plants || []}
                isPlants
                maxChip={100}
                formControlClass=""
                disabled={false}
                invalid={false}
                fieldPath={[]}
              />
              <div className={styles.UsersSelect}>
                <UsersSelection
                  id="create-product-group-coordinators"
                  value={productGroup.coordinators}
                  onChange={(newUsers) => handlePlantChange('coordinators', newUsers)}
                  isMandatory
                  displayInTable={false}
                />
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
      <DialogActions>
        {!fieldsCompleted && (<div className={styles.FieldsMissing}>{t('administration.productGroup.fieldsMissing')}</div>)}
        <Button id="button-create-product-group" variant="contained" color="primary" onClick={saveProductGroup} disabled={!fieldsCompleted}>
          {t('dialog.save')}
        </Button>
        <Button id="button-close-dialog-product-group" color="primary" onClick={close}>
          {t('dialog.cancel')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const mapStateToProps = (state) => ({
  plants: getPlantsForm(state),
});

export default connect(mapStateToProps)(DialogProductGroup);
